import { useCreateFacility } from "../../../api/facility";
import TailwindBottomButtonView from "../../../components/mainBottomButtonView/TailwindBottomButtonView";
import FacilityDrawerFormBody from "./components/FacilityDrawerFormBody";
import FacilityDrawerShell from "./components/FacilityDrawerShell";
import RecordDeleteConfirm from "./components/RecordDeleteConfirm";
import { checkFacilitySaveCondition } from "./helpers/facilityValidation.helpers";
import { useFacilityCountryOptions } from "./hooks/useFacilityCountryOptions";
import { useFacilityDrawerForm } from "./hooks/useFacilityDrawerForm";
import { useFacilitySourceTypeOptions } from "./hooks/useFacilitySourceTypeOptions";
import {
  FacilityDraftFormValues,
  FacilityRegisterModalProps,
  SourceCollectionKey
} from "./types/facilityDrawer.types";
import { buildCreateFacilityPayload } from "./utils/facilityDraft.utils";

const FacilityRegisterDrawer = ({
  visibleModal,
  setVisibleModal,
  refetchFacilityList
}: FacilityRegisterModalProps) => {
  const { energyOptions, heatOptions, waterOptions, refrigerantOptions, isOptionsLoading } =
    useFacilitySourceTypeOptions();
  const { countryOptions, isCountriesLoading } = useFacilityCountryOptions();

  const {
    form,
    energyArray,
    heatArray,
    waterArray,
    refrigerantArray,
    distributionErrors,
    saveDisabled,
    pendingDelete,
    requestDeleteRecord,
    cancelDeleteRecord,
    confirmDeleteRecord,
    addSourceRecord,
    addEnergyRecord,
    submitForm
  } = useFacilityDrawerForm({ visibleModal, mode: "register" });

  const { getValues } = form;
  const { isLoading: isCreating, mutate } = useCreateFacility();

  const closeModal = () => {
    setVisibleModal(false, getValues());
  };

  const onSubmit = (values: FacilityDraftFormValues) => {
    if (checkFacilitySaveCondition("register", values)) {
      return;
    }

    const payload = buildCreateFacilityPayload(values);
    mutate(
      { data: payload, simulatedFacility: false },
      {
        onSuccess: response => {
          refetchFacilityList?.();
          setVisibleModal(false, { newFacilityId: response?._id });
        }
      }
    );
  };

  const sourceFields = {
    heatSource: heatArray.fields,
    waterSource: waterArray.fields,
    refrigerantSource: refrigerantArray.fields
  };

  const sourceOptionsFor = (key: SourceCollectionKey) => {
    if (key === "heatSource") return heatOptions ?? [];
    if (key === "waterSource") return waterOptions ?? [];
    return refrigerantOptions ?? [];
  };

  const removeSourceRecord = (key: SourceCollectionKey, index: number) =>
    requestDeleteRecord(key, index);

  return (
    <FacilityDrawerShell
      open={visibleModal}
      onOpenChange={open => !open && closeModal()}
      eyebrow="Facilities"
      title="Register facility"
      footer={
        <TailwindBottomButtonView
          cancelButtonStatus
          cancelButton={closeModal}
          cancelButtonTitle="Cancel"
          saveButtonStatus
          saveButton={submitForm(onSubmit)}
          saveTitle="Register facility"
          saveDisable={saveDisabled}
          saveLoading={isCreating}
          customHeight={72}
          customBottomContentMain="!tw-px-2 tw-py-3"
          testId="facility-register-bottom-button"
        />
      }
    >
      <FacilityDrawerFormBody
        form={form}
        isOptionsLoading={isOptionsLoading}
        countryOptions={countryOptions}
        isCountriesLoading={isCountriesLoading}
        energyOptions={energyOptions ?? []}
        sourceOptionsFor={sourceOptionsFor}
        energyFields={energyArray.fields}
        sourceFields={sourceFields}
        distributionErrors={distributionErrors}
        onAddEnergyRecord={addEnergyRecord}
        onAddSourceRecord={addSourceRecord}
        onRemoveEnergyRecord={index => requestDeleteRecord("energy", index)}
        onRemoveSourceRecord={removeSourceRecord}
      />

      <RecordDeleteConfirm
        isOpen={pendingDelete !== null}
        onClose={cancelDeleteRecord}
        onConfirm={confirmDeleteRecord}
      />
    </FacilityDrawerShell>
  );
};

export default FacilityRegisterDrawer;
